import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTheme } from '../../contexts/ThemeContext';
import { POSITION_COLORS, POSITIONS } from '../../utils/draftBuildSpectrum';
import {
	buildCompositionRadarChart,
	buildCumulativeAreaChart,
	buildFocusHeatmapChart,
	SPECTRUM_VIEW_MODES
} from '../../utils/draftBuildSpectrumChartData';

export { SPECTRUM_VIEW_MODES };

function SpectrumViewToggle({ view, onChange }) {
	return (
		<div className="flex flex-wrap gap-2">
			{SPECTRUM_VIEW_MODES.map((mode) => (
				<button
					key={mode.id}
					type="button"
					onClick={() => onChange(mode.id)}
					className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
						view === mode.id
							? 'bg-blue-600 text-white border-blue-600'
							: 'bg-gray-100 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-dark-border dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 dark:hover:border-gray-500'
					}`}
				>
					{mode.label}
				</button>
			))}
		</div>
	);
}

function ChartCaption({ children }) {
	return (
		<p className="mt-3 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{children}</p>
	);
}

function ApexSpectrumChart({ chartKey, config, chartEvents }) {
	const options = useMemo(
		() => ({
			...config.options,
			chart: {
				...config.options.chart,
				events: chartEvents
			}
		}),
		[config.options, chartEvents]
	);

	return (
		<Chart
			key={chartKey}
			options={options}
			series={config.series}
			type={config.type}
			height={config.height}
			width="100%"
		/>
	);
}

export function DraftBuildCurveChart({ plan, selectedRound, onRoundSelect }) {
	const { isDarkMode } = useTheme();
	const config = useMemo(() => buildCumulativeAreaChart(plan, { isDarkMode }), [plan, isDarkMode]);

	const chartEvents = useMemo(
		() => ({
			dataPointSelection: (_e, _ctx, cfg) => {
				const round = plan.rounds[cfg.dataPointIndex]?.round;
				if (round) onRoundSelect?.(round);
			}
		}),
		[plan.rounds, onRoundSelect]
	);

	return (
		<div>
			<ApexSpectrumChart
				chartKey={`curve-${plan.spectrumValue}-${plan.constructionMode}-${plan.totalRounds}-${selectedRound}`}
				config={config}
				chartEvents={chartEvents}
			/>
			<ChartCaption>
				Stacked areas show cumulative roster geometry — how fast you accumulate QB, RB, WR, and TE.
				Steep RB early = Zero RB / Hero RB lean; steep WR = Zero WR / Hero WR. Dashed lines mark
				hero revisit, elite anchors, and late QB/TE windows. Click a round to inspect details below.
			</ChartCaption>
		</div>
	);
}

export function DraftFocusHeatmapChart({ plan, selectedRound, onRoundSelect }) {
	const { isDarkMode } = useTheme();
	const config = useMemo(() => buildFocusHeatmapChart(plan, { isDarkMode }), [plan, isDarkMode]);

	const chartEvents = useMemo(
		() => ({
			dataPointSelection: (_e, _ctx, cfg) => {
				const point = cfg.w.config.series[cfg.seriesIndex].data[cfg.dataPointIndex];
				const round = parseInt(String(point?.x || '').replace('R', ''), 10);
				if (round) onRoundSelect?.(round);
			}
		}),
		[onRoundSelect]
	);

	return (
		<div className="w-full min-h-[280px] overflow-x-auto">
			<ApexSpectrumChart
				chartKey={`heat-${plan.spectrumValue}-${plan.constructionMode}-${plan.totalRounds}-${selectedRound}`}
				config={config}
				chartEvents={chartEvents}
			/>
			<ChartCaption>
				Each cell is the engine's focus weight (%) for that position in that round — darker blue
				means stronger conviction. The scheduled primary pick is shown in the tooltip. Hero gaps show
				as cool rows where RB or WR stays quiet before the revisit window.
			</ChartCaption>
		</div>
	);
}

export function DraftRosterShapeChart({ plan }) {
	const { isDarkMode } = useTheme();
	const config = useMemo(() => buildCompositionRadarChart(plan, { isDarkMode }), [plan, isDarkMode]);

	return (
		<div>
			<Chart
				key={`radar-${plan.compositionLine}-${plan.spectrumValue}`}
				options={config.options}
				series={config.series}
				type={config.type}
				height={config.height}
				width="100%"
			/>
			<ChartCaption>
				Position-colored spokes (QB top · RB left · WR right · TE bottom). This build on each
				position scale — outer edge is full scale.
			</ChartCaption>
			<div className="mt-4 flex flex-wrap gap-2 justify-center">
				{POSITIONS.map((pos) => (
					<span
						key={pos}
						className={`text-xs font-mono px-2 py-1 rounded border ${POSITION_COLORS[pos].border} ${POSITION_COLORS[pos].soft} ${POSITION_COLORS[pos].text}`}
					>
						{pos} {plan.rosterProjection[pos]}
					</span>
				))}
			</div>
		</div>
	);
}

export function DraftSpectrumVisualPanel({
	plan,
	view,
	selectedRound,
	onViewChange,
	onRoundSelect,
	children
}) {
	return (
		<div className="p-5 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card">
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
				<h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
					{SPECTRUM_VIEW_MODES.find((m) => m.id === view)?.label || 'Round map'}
				</h3>
				<SpectrumViewToggle view={view} onChange={onViewChange} />
			</div>

			{view === 'map' && children}

			{view === 'curve' && (
				<DraftBuildCurveChart
					plan={plan}
					selectedRound={selectedRound}
					onRoundSelect={onRoundSelect}
				/>
			)}

			{view === 'heatmap' && (
				<DraftFocusHeatmapChart
					plan={plan}
					selectedRound={selectedRound}
					onRoundSelect={onRoundSelect}
				/>
			)}

			{view === 'shape' && <DraftRosterShapeChart plan={plan} />}
		</div>
	);
}
